/**
 * Record and play back — captures what the child taps (notes with timing) and
 * plays it back later over the same AudioEngine.
 *
 * Data model: a recording is a sequence of `{ key, atMs }` events
 * where `atMs` is the offset in milliseconds from the FIRST note (the first
 * note is always 0). Storing the offset from the start —and not the duration
 * between notes— makes playback scheduling trivial and is robust to
 * long pauses between taps.
 *
 * The clock is injected (`now`/`schedule`) just like in the song player,
 * so that tests use a fake clock without real timers.
 *
 * CC-6: this file is pure `.ts` (no lingui macros), logic only.
 */

import type { AudioEngine, InstrumentId, SoundKey } from "../audio/types";

export interface RecordedNote {
  key: SoundKey;
  /** Offset in ms from the first note (the first is always 0). */
  atMs: number;
}

export interface Recording {
  instrument: InstrumentId;
  notes: RecordedNote[];
}

export interface RecorderClock {
  now: () => number;
  /** Schedules `fn` in `ms`; returns its canceller. */
  schedule: (fn: () => void, ms: number) => () => void;
}

const realClock: RecorderClock = {
  now: () => performance.now(),
  schedule: (fn, ms) => {
    const id = setTimeout(fn, ms);
    return () => {
      clearTimeout(id);
    };
  },
};

export interface Recorder {
  /** Starts a new recording (discards whatever was captured in progress). */
  start: () => void;
  /** Records a note played right now. No-op if not recording. */
  capture: (key: SoundKey) => void;
  /** Closes the recording and returns it (null if no note was captured). */
  stop: () => Recording | null;
  isRecording: () => boolean;
}

export function createRecorder(
  instrument: InstrumentId,
  clock: RecorderClock = realClock,
): Recorder {
  let recording = false;
  let startedAt = 0;
  let notes: RecordedNote[] = [];

  return {
    start: () => {
      recording = true;
      startedAt = clock.now();
      notes = [];
    },
    capture: (key) => {
      if (!recording) {
        return;
      }
      const at = clock.now();
      if (notes.length === 0) {
        // The first note anchors time zero (ignores the gap until the
        // first tap after pressing record).
        startedAt = at;
      }
      notes.push({ key, atMs: at - startedAt });
    },
    stop: () => {
      recording = false;
      if (notes.length === 0) {
        return null;
      }
      return { instrument, notes };
    },
    isRecording: () => recording,
  };
}

export interface RecordingPlayerCallbacks {
  /** Each note as it sounds (for visual feedback: key lit up). */
  onNote?: (note: RecordedNote, index: number) => void;
  /** Playback finished (or was stopped manually). */
  onFinished?: () => void;
}

export interface RecordingPlayback {
  /** True while there are notes left to sound. */
  isPlaying: () => boolean;
  /** Stops playback and cuts the voices. */
  stop: () => void;
}

/**
 * Plays a recording by firing each note at its `atMs` on the engine.
 * Returns a handle to stop it. Playback starts immediately.
 */
export function playRecording(
  engine: AudioEngine,
  recording: Recording,
  callbacks: RecordingPlayerCallbacks = {},
  clock: RecorderClock = realClock,
): RecordingPlayback {
  let stopped = false;
  const cancels: (() => void)[] = [];
  let remaining = recording.notes.length;

  const finishIfDone = () => {
    if (remaining <= 0 && !stopped) {
      stopped = true;
      callbacks.onFinished?.();
    }
  };

  if (recording.notes.length === 0) {
    queueMicrotask(finishIfDone);
  }

  recording.notes.forEach((note, index) => {
    const cancel = clock.schedule(() => {
      if (stopped) {
        return;
      }
      engine.playNote(recording.instrument, note.key);
      callbacks.onNote?.(note, index);
      remaining -= 1;
      finishIfDone();
    }, note.atMs);
    cancels.push(cancel);
  });

  return {
    isPlaying: () => !stopped,
    stop: () => {
      if (stopped) {
        return;
      }
      stopped = true;
      for (const cancel of cancels) {
        cancel();
      }
      engine.allNotesOff();
      callbacks.onFinished?.();
    },
  };
}

/**
 * Persistence of the LAST recording in localStorage (no sync; losing it on
 * clearing data is acceptable, just like progress). A single slot: it records
 * over the previous one, like a toy recorder.
 */

const STORAGE_KEY = "recording.v1";

interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

export interface RecordingStore {
  save: (recording: Recording) => void;
  load: () => Recording | null;
  clear: () => void;
}

export function createRecordingStore(storage?: StorageLike): RecordingStore {
  const backend: StorageLike | null =
    storage ?? (typeof localStorage === "undefined" ? null : localStorage);

  return {
    save: (recording) => {
      try {
        backend?.setItem(STORAGE_KEY, JSON.stringify(recording));
      } catch {
        // storage full/blocked: the recording lives only in session memory
      }
    },
    load: () => {
      if (!backend) {
        return null;
      }
      try {
        const raw = backend.getItem(STORAGE_KEY);
        if (!raw) {
          return null;
        }
        const parsed: unknown = JSON.parse(raw);
        return isRecording(parsed) ? parsed : null;
      } catch {
        return null;
      }
    },
    clear: () => {
      try {
        backend?.removeItem(STORAGE_KEY);
      } catch {
        // no-op
      }
    },
  };
}

function isRecording(value: unknown): value is Recording {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const record = value as Partial<Recording>;
  if (
    record.instrument !== "piano" &&
    record.instrument !== "xylophone" &&
    record.instrument !== "drums"
  ) {
    return false;
  }
  if (!Array.isArray(record.notes)) {
    return false;
  }
  return record.notes.every(isRecordedNote);
}

function isRecordedNote(value: unknown): value is RecordedNote {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const note = value as Partial<RecordedNote>;
  return typeof note.key === "string" && typeof note.atMs === "number";
}
